import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Modal, Tabs, Tab, Accordion, Spinner, Card } from 'react-bootstrap';
import { getInfoSampling, getSamplingHistory, getSamplingStatistics, getAvailableSalForSampling, createSampling } from '@lib/api';
import { OETrans } from 'components/translation/OETrans';
import { ArrowLeft, IcoDownloadCloudArrow, ExclamationBrown, Trash, InfoCircleFill } from '@icons';
import ExternalSupplies from './Sampling/ExternalSupplies';
import DropdownSals from './Sampling/DropdownSals';
import ModalDownloadReport from './Sampling/ModalDownloadReport';
import OwnershipModal from './OwnershipModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import SuccessModal from '@components/atomic/organisms/SucessModal/SucessModal';
import { RejectModal } from '@components/atomic/organisms';
import StatisticsHeader from './Sampling/StatisticsHeader';
import InternalSupplies from './Sampling/InternalSupplies';
import { formatToDecimals } from '@lib/helpers/formatting';

const ExecutionSummarySampling = ({ project }) => {
	const [sampling, setSampling] = useState({
		id: null,
		infoPreSampling: {},
		tempInfoPreSampling: null,
		samplingList: [],
		originSelectedSals: [],
		statistics: {},
	});
	const [selectedSals, setSelectedSals] = useState([]);
	const [externalSupplies, setExternalSupplies] = useState([]);
	const [showModalSampling, setShowModalSampling] = useState(false);
	const [showModalDownloadReport, setShowModalDownloadReport] = useState(false);
	const [activeKey, setActiveKey] = useState('external-supplies');
	// const [isDoneSamplingWithCompleteList, setIsDoneSamplingWithCompleteList] = useState(false);
	// const [isFiltered, setIsFiltered] = useState(false);
	// const [modelFilterString, setModelFilterString] = useState(''); //per filtri
	const [filterQueryParamsString, setFilterQueryParamsString] = useState(''); //per filtri
	const [externalSuppliesMeta, setExternalSuppliesMeta] = useState();
	let dynamicInfoSampling = sampling?.tempInfoPreSampling ? sampling?.tempInfoPreSampling : sampling?.infoPreSampling;
	let isDefaultSampling = project?.samplingRuleAssignment?.samplingRule?.code === 'ITALIA_DEFAULT';
	// console.log('sampling', sampling);
	
	const queryClient = useQueryClient();
	const handleShowModal = (setShowModal) => setShowModal(true);

	const handleCloseModal = (setShowModal) => {
		setShowModal(false);

		setSampling((prev) => ({
			...prev,
			tempInfoPreSampling: null,
		}));
	};

	const { data: samplingHistory, isLoading: isLoadingSamplingHistory } = useQuery({
		queryKey: ['samplingHistory', project.id],
		queryFn: async () => {
			const result = await getSamplingHistory(project.id, 0);

			if (!result.success) {
				throw new Error('Failed to fetch sampling history');
			}

			const samplingList = result.data;
			const id = samplingList.find((el) => !el.completed)?.id ?? null;

			return { samplingList, id };
		},
		enabled: !!project.id,
	});

	const {
		data: optionsSals,
		// isLoading: isLoadingSals,
		// isError: isErrorSals,
	} = useQuery({
		queryKey: ['availableSals', project.id],
		queryFn: async () => {
			const result = await getAvailableSalForSampling(project.id);
			if (!result.success) {
				throw new Error('Failed to fetch available SALs');
			}
			return result.data.map((sal) => ({
				value: sal,
				label: sal,
			}));
		},
		enabled: !!project.id,
	});

	const { data: infoSampling, isLoading: isLoadingInfoSampling } = useQuery({
		queryKey: ['infoSampling', project.id],
		queryFn: async () => {
			const result = await getInfoSampling(project.id);
			if (!result.success) {
				throw new Error('Failed to fetch sampling info');
			}
			return result.data;
		},
		enabled: !!project.id,
	});

	const {
		data: samplingStatistics,
		isLoading: isLoadingSamplingStatistics,
		// 		isError: isErrorSamplingStatistics,
	} = useQuery({
		queryKey: ['samplingStatistics', project.id, sampling.id],
		queryFn: async () => {
			const result = await getSamplingStatistics(project.id, sampling.id);
			if (!result.success) {
				throw new Error('Failed to fetch sampling statistics');
			}
			return result.data;
		},
		enabled: !!project.id && !!sampling.id,
	});

	const tempInfoSamplingMutation = useMutation({
		mutationFn: (selected) => getInfoSampling(project.id, selected),
		onSuccess: (result) => {
			if (result.success) {
				setSampling((prev) => ({
					...prev,
					tempInfoPreSampling: result.data,
				}));
			}
		},
		onError: (error) => {
			console.error('Error extending sampling:', error);
		},
	});

	useEffect(() => {
		if (samplingHistory || infoSampling || samplingStatistics) {
			setSampling((prev) => ({
				...prev,
				...(samplingHistory ?? {}),
				infoPreSampling: infoSampling ?? prev.infoPreSampling,
				statistics: samplingStatistics ?? prev.statistics,
			}));

			// if (samplingHistory) {
			// 	setIsDoneSampling(samplingHistory.samplingList.length > 0);
			// }
		}
	}, [samplingHistory, infoSampling, samplingStatistics]);

	useEffect(() => {
		if (!optionsSals || optionsSals.length === 0) return;
		if (sampling.statistics.sals?.length > 0) {
			let tempSelectedSals = optionsSals.filter((option) => {
				return sampling.statistics.sals.some((sal) => {
					return sal === option.value;
				});
			});

			setSelectedSals(tempSelectedSals);
			setSampling((prev) => ({ ...prev, originSelectedSals: tempSelectedSals }));
		}
	}, [optionsSals, sampling.statistics.sals]);

	const handleTempInfoSampling = async (selected) => {
		tempInfoSamplingMutation.mutate(selected);
	};

	const removeSal = (salValue) => {
		const updatedSelectedSals = selectedSals.filter((sal) => sal.value !== salValue);
		setSelectedSals(updatedSelectedSals);

		handleTempInfoSampling(updatedSelectedSals);
	};

	const saveSamplingMutation = useMutation({
		mutationFn: async () => {
			const result = await createSampling(project.id, { sals: selectedSals.map((sal) => sal.value) });
			if (!result.success) {
				throw new Error('Failed to create sampling');
			}
			return result;
		},
		onSuccess: (result) => {
			setSampling((prev) => ({ ...prev, originSelectedSals: selectedSals }));
			setSelectedSals(result.sample.info.sals.map((sal) => ({ value: sal, label: sal })));
			setExternalSupplies(result.data);
			queryClient.invalidateQueries(['samplingHistory', project.id]);
			queryClient.invalidateQueries(['infoSampling', project.id]);

			if (sampling.id) {
				queryClient.invalidateQueries(['samplingStatistics', project.id, sampling.id]);
			}
			setShowModalSampling(false);
		},
		onError: (error) => {
			console.error('Error saving sampling:', error);
		},
	});

	const saveSals = async () => {
		if (selectedSals.length > 0) {
			saveSamplingMutation.mutate();
		}
	};

	const isLoadingExternalSupplies = isLoadingSamplingHistory || isLoadingInfoSampling || isLoadingSamplingStatistics;

	return (
		<>
			{activeKey === 'external-supplies' && !isLoadingExternalSupplies ? (
				<StatisticsHeader
					isActiveSampling={samplingHistory.samplingList.length > 0}
					handleShowModal={handleShowModal}
					setShowModalSampling={setShowModalSampling}
					statistics={sampling.statistics}
					projectId={project.id}
					sampleId={sampling.id}
					project={project}
					setExternalSupplies={setExternalSupplies}
					samplingInfoPreSampling={sampling.infoPreSampling}
					invoicesLength={externalSupplies?.length}
				/>
			) : null}

			<div className="simple-box mt-0" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
				<Row className="align-items-center mb-2">
					<Col className="p-0">
						<Tabs
							activeKey={activeKey}
							variant="pills"
							id="uncontrolled-tab-example"
							className="mb-3"
							onSelect={(key) => {
								setActiveKey(key);
								// setIsFiltered(false);
								// setModelFilterString('');
							}}
						>
							<Tab eventKey="external-supplies" title="Costi esterni"></Tab>
							<Tab eventKey="intern-workers" title="Costi interni"></Tab>
						</Tabs>
					</Col>

					<Col xs="auto" className="p-0 d-flex gap-3">
						{activeKey === 'external-supplies' && (
							<>
								<Button
									onClick={() => handleShowModal(setShowModalDownloadReport)}
									className="btn new custom-outline-button"
									style={{
										height: '48px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '285px',
									}}
								>
									Scarica un report
									<IcoDownloadCloudArrow width="20" height="21" />
								</Button>

								<OwnershipModal
									disabled={isLoadingExternalSupplies}
									externalSuppliesCount={externalSupplies?.length}
									samplingId={sampling.id}
								/>
							</>
						)}
					</Col>
				</Row>

				<Row>
					<Col className="p-0">
						{activeKey === 'external-supplies' && !isLoadingExternalSupplies && (
							<ExternalSupplies
								project={project}
								activeKey={activeKey}
								externalSupplies={externalSupplies}
								setExternalSupplies={setExternalSupplies}
								isActiveSampling={samplingHistory.samplingList.length > 0}
								// isDoneSamplingWithCompleteList={isDoneSamplingWithCompleteList}
								externalSuppliesMeta={externalSuppliesMeta}
								setExternalSuppliesMeta={setExternalSuppliesMeta}
								sampling={sampling}
								setFilterQueryParamsString={setFilterQueryParamsString}
							/>
						)}
						{activeKey === 'intern-workers' && !isLoadingExternalSupplies && (
							<InternalSupplies
								project={project}
								activeKey={activeKey}
								isActiveSampling={samplingHistory.samplingList.length > 0}
								sampling={sampling}
							/>
						)}
					</Col>
				</Row>
			</div>

			<Modal
				fullscreen
				className="new-modal"
				show={showModalSampling}
				onHide={() => {
					setSelectedSals(sampling.originSelectedSals);
					handleCloseModal(setShowModalSampling);
				}}
				centered
			>
				<Modal.Header style={{ padding: '32px 48px' }}>
					<Modal.Title style={{ fontWeight: '700' }}>Configurazione del campionamento</Modal.Title>
					<Button
						variant="link"
						className="btn ms-auto no-underline"
						onClick={() => {
							setSelectedSals(sampling.originSelectedSals);
							handleCloseModal(setShowModalSampling);
						}}
					>
						<ArrowLeft className="me-2" />
						<span style={{ color: '#06797F', verticalAlign: 'middle' }}>Indietro</span>
					</Button>
				</Modal.Header>
				<Modal.Body style={{ padding: '10px 48px 32px 48px' }}>
					<Row className="mb-4">
						<span className="new-subtitle fs-28">PROGETTO: {project.name}</span>
					</Row>

					<Accordion className="new">
						<Accordion.Item eventKey="0">
							<Accordion.Header>
								<div className="d-flex w-100 align-items-center fs-18">
									<span className="f-600">Regole di campionamento</span>
									<span className="ms-auto me-2 new-text-as-link">espandi per i dettagli</span>
								</div>
							</Accordion.Header>
							<Accordion.Body>
								<span className="new-subtitle fs-24">{project?.samplingRuleAssignment?.samplingRule?.name}</span>
								<span className="d-block mt-2">{project?.samplingRuleAssignment?.samplingRule?.description}</span>
							</Accordion.Body>
						</Accordion.Item>
					</Accordion>

					<Row className="my-4 align-items-center">
						<Col className="col-auto">
							<span className="new-subtitle fs-24 align-middle">Parametri per il campionamento</span>
						</Col>
					</Row>

					<Row className="mb-4">
						<span className="">
							Per facilitare questa operazione il sistema ha inserito dei valori standard ritenuti conformi alle regole del bando.
							<br />
							Puoi personalizzare i parametri rispetto alle esigenze del progetto
						</span>
					</Row>
					<Card className="new">
						<Card.Body>
							<Row className="align-items-center my-2">
								<Col className="col-6 me-auto">
									<Row className="flex-column">
										<Col className="">
											<span className="new-subtitle">SAL</span>
										</Col>
										<Col>
											<span className="d-block mt-2">
												Lo Stato Avanzamento Lavori rappresenta una fase del progetto a cui sono associati documenti come fatture, contratti
												e relazioni. Ogni SAL definisce un gruppo documentale su cui è possibile applicare il campionamento.
											</span>
										</Col>
									</Row>
								</Col>

								<Col className="col-4">
									{selectedSals.length > 0 && (
										<Row className="align-items-center justify-content-end text-center">
											{selectedSals.map((sal, index) => (
												<Col key={index} className="col-4 py-3">
													<div className="new-badge" style={{ justifySelf: 'center' }}>
														<span style={{ verticalAlign: 'sub' }}>{sal.label}</span>
														<Button variant="link" className="ms-1" onClick={() => removeSal(sal.value)}>
															<Trash className="ms-1 ico-path-fill-red" style={{ width: '20px' }} />
														</Button>
													</div>
												</Col>
											))}
										</Row>
									)}
								</Col>

								<Col className="col-auto">
									<DropdownSals
										optionsSals={optionsSals}
										originSelectedSals={sampling.originSelectedSals}
										selectedSals={selectedSals}
										setSelectedSals={setSelectedSals}
										setShowModalSampling={setShowModalSampling}
										saveSals={saveSals}
										handleTempInfoSampling={handleTempInfoSampling}
									/>
								</Col>
							</Row>
						</Card.Body>
					</Card>

					<div className="container-vertical-line">
						<div className="vertical-border-line"></div>
						<div className="vertical-line-cards w-100">
							<Card className={`new mt-0 mb-0 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`} style={{ color: '#4A5968' }}>
								<Card.Body>
									<Row className="align-items-center my-2">
										<Col className="col-7 me-auto">
											<Row className="flex-column">
												<Col className="">
													<span className="f-700">Importo rendicontato</span>
												</Col>
												<Col>
													<span className="d-block mt-2">
														Valore economico totale già rendicontato nei documenti associati al SAL selezionato. Questo parametro aiuta a
														comprendere il volume finanziario dei documenti inclusi nel campione.
													</span>
												</Col>
											</Row>
										</Col>

										<Col className="col-auto">
											<span className="f-700" style={{ fontSize: '2rem' }}>
												{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
													? '-'
													: formatToDecimals(dynamicInfoSampling?.calculationDetails?.reportedAmount)}{' '}
												€
											</span>
										</Col>
									</Row>
								</Card.Body>
							</Card>

							{!isDefaultSampling && (
								<>
									<Card
										className={`new mt-4 mb-4 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`}
										style={{ color: '#4A5968' }}
									>
										<Card.Body>
											<Row className="align-items-center my-2">
												<Col className="col-7 me-auto">
													<Row className="flex-column">
														<Col className="">
															<span className="f-700">Numerosità dei soggetti fornitori</span>
														</Col>
														<Col>
															<span className="d-block mt-2">
																Numero di fornitori coinvolti nei documenti associati al SAL. Questo parametro può indicare la varietà e
																complessità delle relazioni contrattuali da analizzare.
															</span>
														</Col>
													</Row>
												</Col>

												<Col className="col-auto">
													<span className="f-700" style={{ fontSize: '2rem', color: '#4A5968' }}>
														{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
															? '-'
															: formatToDecimals(dynamicInfoSampling?.calculationDetails?.suppliersCount, 0, 0)}
													</span>
												</Col>
											</Row>
										</Card.Body>
									</Card>

									<Card
										className={`new mt-0 mb-4 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`}
										style={{ color: '#4A5968' }}
									>
										<Card.Body>
											<Row className="align-items-center my-2">
												<Col className="col-7 me-auto">
													<Row className="flex-column">
														<Col className="">
															<span className="f-700">Numerosità delle tipologie delle voci di spesa</span>
														</Col>
														<Col>
															<span className="d-block mt-2">
																Indica quante categorie di spesa sono presenti nei documenti associati al progetto (es. consulenze,
																forniture, lavori). Aiuta a valutare la varietà del contenuto economico da campionare.
															</span>
														</Col>
													</Row>
												</Col>

												<Col className="col-auto">
													<span className="f-700" style={{ fontSize: '2rem', color: '#4A5968' }}>
														{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
															? '-'
															: formatToDecimals(dynamicInfoSampling?.calculationDetails?.expenseTypesCount, 0, 0)}
													</span>
												</Col>
											</Row>
										</Card.Body>
									</Card>

									<Card
										className={`new mt-0 mb-0 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`}
										style={{ color: '#4A5968' }}
									>
										<Card.Body>
											<Row className="align-items-center my-2">
												<Col className="col-7 me-auto">
													<Row className="flex-column">
														<Col className="">
															<span className="f-700">Numerosità dei comuni/siti/aree esposte a rendiconto</span>
														</Col>
														<Col>
															<span className="d-block mt-2">
																Numero di comuni o aree territoriali coinvolti nei documenti associati al progetto. Utile per valutare la
																distribuzione geografica del progetto.
															</span>
														</Col>
													</Row>
												</Col>

												<Col className="col-auto">
													<span className="f-700" style={{ fontSize: '2rem', color: '#4A5968' }}>
														{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
															? '-'
															: formatToDecimals(dynamicInfoSampling?.calculationDetails?.municipalitiesCount, 0, 0)}
													</span>
												</Col>
											</Row>
										</Card.Body>
									</Card>
								</>
							)}
						</div>
					</div>

					{!isDefaultSampling && (
						<div className="container-vertical-line mt-5">
							<div className="vertical-border-line"></div>
							<div className="vertical-line-cards">
								<Row>
									<Col>
										<Card
											className={`new mt-0 mb-4 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`}
											style={{ color: '#4A5968' }}
										>
											<Card.Body>
												<Row className="align-items-center my-2">
													<Col className="col-8 me-auto">
														<Row className="flex-column">
															<Col className="">
																<span className="f-700">Identificazione del rischio intrinseco</span>
															</Col>
															<Col>
																<span className="d-block mt-2">
																	Valuta la probabilità che i documenti presentino errori, omissioni o anomalie, in base alla loro natura e
																	complessità.
																</span>
															</Col>
														</Row>
													</Col>

													<Col className="col-auto">
														<span className="f-700" style={{ fontSize: '2rem', color: '#4A5968' }}>
															{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
																? '-'
																: formatToDecimals(dynamicInfoSampling?.intrinsicRisk, 0, 0)}
														</span>
													</Col>
												</Row>
											</Card.Body>
										</Card>
									</Col>

									<Col>
										<Card
											className={`new mt-0 mb-4 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`}
											style={{ color: '#4A5968' }}
										>
											<Card.Body>
												<Row className="align-items-center my-2">
													<Col className="col-8 me-auto">
														<Row className="flex-column">
															<Col className="">
																<span className="f-700">Rischio di controllo</span>
															</Col>
															<Col>
																<span className="d-block mt-2">
																	Indica quanto è probabile che eventuali irregolarità nei documenti non vengano rilevate durante le
																	verifiche previste.
																</span>
															</Col>
														</Row>
													</Col>

													<Col className="col-auto">
														<span className="f-700" style={{ fontSize: '2rem', color: '#4A5968' }}>
															{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
																? '-'
																: formatToDecimals(dynamicInfoSampling?.controlRisk, 0, 0)}
														</span>
													</Col>
												</Row>
											</Card.Body>
										</Card>
									</Col>
								</Row>

								<Card
									className={`new mt-0 mb-0 ${selectedSals.length === 0 ? 'background-card-disabled' : ''}`}
									style={{ color: '#4A5968' }}
								>
									<Card.Body>
										<Row className="align-items-center my-2">
											<Col className="col-7 me-auto">
												<Row className="flex-column">
													<Col className="">
														<span className="f-700">Rischio Totale</span>
													</Col>
													<Col>
														<span className="d-block mt-2">
															Parametro che riflette la probabilità che eventuali errori o irregolarità non vengano rilevati durante le
															verifiche. Può dipendere da fattori come la qualità dei controlli o la complessità dei documenti.
														</span>
													</Col>
												</Row>
											</Col>

											<Col className="col-auto">
												<span className="f-700" style={{ fontSize: '2rem', color: '#4A5968' }}>
													{selectedSals.length === 0 || tempInfoSamplingMutation.isPending
														? '-'
														: dynamicInfoSampling?.calculationDetails?.riskLevel}
												</span>
											</Col>
										</Row>
									</Card.Body>
								</Card>
							</div>
						</div>
					)}

					{selectedSals.length > 0 && (
						<>
							<Row className="mt-5 mb-3 mx-0 justify-content-center">
								<Col
									className="col-auto first-part-info-banner"
									style={{
										borderRadius: '8px',
									}}
								>
									<Row className="align-items-end">
										<Col className="col-auto pe-2">
											<InfoCircleFill
												style={{
													translate: '0 -1px',
												}}
											/>
										</Col>
										<Col className="col-auto ps-0 my-auto">
											{/* <span className="f-700 text-uppercase d-block" style={{ color: '#2A3A64' }}>
												Qualora le informazioni non risultino coerenti, effettua nuovamente il caricamento del template dati.
											</span> */}
											<span className="f-700 text-uppercase" style={{ color: '#2A3A64' }}>
												Qualora le informazioni non risultino coerenti{' '}
												<a
													href="mailto:support.openrep@openeconomics.eu"
													style={{
														color: '#06797F',
													}}
												>
													contatta il supporto
												</a>
											</span>
										</Col>
									</Row>
								</Col>

								{/* <Col className="col-auto second-part-info-banner">
									<span className="f-700">
										in alternativa,{' '}
										<Button variant="link" href="mailto:support.openrep@openeconomics.eu">
											contatta l'assistenza
										</Button>
									</span>
								</Col> */}
							</Row>
						</>
					)}
				</Modal.Body>

				<Modal.Footer
					className="flex-column w-100 p-0"
					style={{
						...(selectedSals.length > 0 ? { boxShadow: '0px -2px 10px 0px #00000040' } : {}),
						zIndex: 10,
					}}
				>
					{selectedSals.length === 0 && (
						<div
							className="new-alert-banner border-end-0 border-start-0 rounded-0 d-flex justify-content-center align-items-center w-100 py-3 my-0"
							style={{ marginLeft: '-3px' }}
						>
							<ExclamationBrown fill="#995C00" width={20} height={20} />
							<span className="f-700 text-uppercase mx-2" style={{ color: '#995C00' }}>
								ATTENZIONE:
							</span>
							<span className="f-700">Seleziona almeno un SAL per vedere i risultati dei calcoli.</span>
						</div>
					)}

					<Row className="w-100 align-items-center mt-3" style={{ padding: '12px' }}>
						<Col className="text-start">
							<Row className="align-items-center">
								<Col className="col-auto">
									<span style={{ fontWeight: '700', lineHeight: '28px' }}>
										Risultato applicando
										<br />
										le regole ed i parametri:
									</span>
								</Col>

								<Col className="col-auto ms-4" style={{ fontWeight: '700', fontSize: '40px', color: '#06797F' }}>
									<span>{selectedSals.length === 0 || tempInfoSamplingMutation.isPending ? '-' : (dynamicInfoSampling?.base ?? 0)}</span>
								</Col>

								<Col className="col-auto ps-0 pe-2" style={{ fontWeight: '700', color: '#06797F' }}>
									<span>
										catene documentali
										<br />
										da controllare
									</span>
								</Col>

								<Col className="col-auto px-0" style={{ fontSize: '60px', color: '#4A5968' }}>
									<span>/</span>
								</Col>

								<Col className="col-auto" style={{ fontWeight: '700', fontSize: '40px', color: '#4A5968' }}>
									<span>
										{selectedSals.length === 0 || tempInfoSamplingMutation.isPending ? '-' : (dynamicInfoSampling?.population ?? 0)}
									</span>
								</Col>

								<Col className="col-auto px-0" style={{ fontWeight: '400', color: '#4A5968' }}>
									<span>
										catene documentali
										<br />
										totali nel progetto
									</span>
								</Col>
							</Row>
						</Col>
						<Col className="text-end col-auto">
							<Button
								className="new custom-outline-button me-3"
								style={{ fontWeight: '500', width: '285px', height: '48px' }}
								onClick={() => {
									setSelectedSals(sampling.originSelectedSals);
									setShowModalSampling(false);
								}}
							>
								<OETrans code="common.cancel" fallback={'Annulla'} />
							</Button>
							<Button
								variant="primary"
								className="new custom-primary-button"
								style={{ fontWeight: '500', width: '285px', height: '48px' }}
								onClick={() => saveSals()}
								disabled={selectedSals.length === 0}
							>
								Conferma
								{selectedSals.length > 0 && (saveSamplingMutation.isPending || tempInfoSamplingMutation.isPending) && (
									<Spinner
										as="span"
										className="ms-2 ico-path-fill-white"
										animation="border"
										size="sm"
										role="status"
										aria-hidden="true"
										style={{ verticalAlign: 'revert-layer' }}
									/>
								)}
							</Button>
						</Col>
					</Row>
				</Modal.Footer>
			</Modal>

			{showModalDownloadReport && (
				<ModalDownloadReport
					projectId={project.id}
					// modelFilterString={modelFilterString}
					// isFiltered={isFiltered}
					filterQueryParamsString={filterQueryParamsString}
					showModalDownloadReport={showModalDownloadReport}
					setShowModalDownloadReport={setShowModalDownloadReport}
				/>
			)}

			<SuccessModal
				show={saveSamplingMutation.isSuccess}
				onHide={saveSamplingMutation.reset}
				message="Campionamento eseguito con successo!"
			/>

			<RejectModal
				show={saveSamplingMutation.isError}
				onHide={saveSamplingMutation.reset}
				message="Si è verificato un errore durante il campionamento."
				tryAgain={saveSamplingMutation.mutate}
			/>
		</>
	);
};

export default ExecutionSummarySampling;
